import { inject, injectable } from "inversify";
import { CatalogData } from "../../data/catalogData";
import ICatalogService from "./ICatalogService";
import { CatalogApi } from "../api/CatalogApi";
import { DiTokens } from "../diTokens";
import IAlertService from "../alert/IAlertService";
import IErrorService from "../error/IErrorService";
import { ChargeData } from "../../data/chargeData";
import { Region } from '../../data/regionsData';
import { BankProductData } from "../../data/bankProductData";
import { DepositData } from "../../data/depositData";
import { DepositCurrencyTypeData } from "../../data/depositCurrencyTypeData";
import { CreditProductData } from "../../data/creditProductData";
import i18n from "@/i18n";

@injectable()
/** Сервис каталога операций */
export default class CatalogService implements ICatalogService {
  constructor(
    @inject(DiTokens.AlertService) private alertService: IAlertService,
    @inject(DiTokens.ErrorService) private errorService: IErrorService
  ) {}

  public async getCatalogAsync(id: number): Promise<CatalogData> {
    let resCatalog = new CatalogData();
    try {
      resCatalog = await CatalogApi.getCatalogAsync(id);
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return resCatalog;
  }

  public async fetchBankProducts(): Promise<Array<BankProductData>> {
    try {
      const resp: any = await CatalogApi.getBankProducts();
      let products: Array<BankProductData> = [];

      for (const key in resp.data?.Data?.InnerData?.Data) {
        const product: any = resp.data?.Data?.InnerData?.Data[key];

        if (product) {

          products.push(new BankProductData(product.Properties, product.InnerData?.Data?.detailNames?.Properties));

          const lastAddedProduct: BankProductData = products[products.length-1];

          if (lastAddedProduct.ViewName === 'depositTypesList') {

            for (const i in product.InnerData?.Data?.childs?.InnerData?.Data) {

              const deposit: any = product.InnerData?.Data?.childs?.InnerData?.Data[i];

              lastAddedProduct.Childs.push(new DepositData(deposit.Properties));

              for (const j in deposit.InnerData?.Data) {

                const currDepo: any = deposit.InnerData?.Data[j];

                lastAddedProduct.Childs[lastAddedProduct.Childs.length - 1].CurrencyChilds.push(
                  new DepositCurrencyTypeData(currDepo.Properties, currDepo.InnerData?.Data?.initData?.Properties)
                );

              }

              lastAddedProduct.Childs[lastAddedProduct.Childs.length - 1].ActiveCurrency = 
                lastAddedProduct.Childs[lastAddedProduct.Childs.length - 1].CurrencyChilds[0].currency;

              lastAddedProduct.Childs[lastAddedProduct.Childs.length - 1].ActiveTerm = 
                lastAddedProduct.Childs[lastAddedProduct.Childs.length - 1].CurrencyChilds[0].term;

            }

          } else if (lastAddedProduct.ViewName === 'creditTypesList') {
            for (const i in product.InnerData?.Data?.childs?.InnerData?.Data) {

              const credit: any = product.InnerData?.Data?.childs?.InnerData?.Data[i];

              lastAddedProduct.Childs.push(new CreditProductData(credit.Properties));

            }
          }

        }
      }

      return products;

    } catch (e) {
      await this.errorService.errorHandler(e, false, false, "");
    }    
  }

  public async searchCatalog(
    query: string,
    city: string
  ): Promise<CatalogData> {
    let resCatalog = new CatalogData();
    try {
      resCatalog = await CatalogApi.searchCatalog(query, city);
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return resCatalog;
  }

  public async searchCatalogExt(
    query: string,
    city: string
  ): Promise<CatalogData> {
    let resCatalog = new CatalogData();
    try {
      resCatalog = await CatalogApi.searchCatalogExt(query, city);
      if (!resCatalog.Childrens || !resCatalog.Childrens.length) {
        this.alertService.info('По вашему запросу ничего не найдено.');
        return await this.getCatalogAsync(0);
      }
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return resCatalog;
  }

  public async fetchRegions(): Promise<Array<Region>> {
    try {
      const regions = await CatalogApi.getRegions();
      if (regions)
        return regions;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
  }

  public async fetchCharges(isAllChargesVisible: boolean = false): Promise<Array<ChargeData>> {
    try {
      const charges: Array<ChargeData> = await CatalogApi.getCharges(isAllChargesVisible);
      if (charges)
        return charges;
    } catch (e) {
      await this.alertService.error(i18n.t("main.charges.errors.loadError", { errorText: e.message }).toString());
    }
  }
}
